/*  Colega Link · PioneroChallengeCard
    Tarjeta compartida entre Dashboard e Invitar que muestra:
    - Barra de progreso del Reto Pionero (X/15 refs validados)
    - Días restantes del plazo
    - Lista de referidos con estado (Verificado / Pendiente)
    - Botón QR de invitación
    Se autoconsulta /plans/status y /plans/my-referrals.
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Trophy, Users, Clock, CheckCircle2, CircleDashed, X, QrCode, Copy, Check, Download, Info,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const buildRefUrl = (code) => {
  if (!code) return null;
  const origin = (typeof window !== "undefined" && window.location?.origin) || "https://www.colegalink.cl";
  return `${origin}/registro/abogado?ref=${encodeURIComponent(code)}`;
};

const daysAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return "hoy";
  if (diff === 1) return "ayer";
  if (diff < 7) return `hace ${diff}d`;
  if (diff < 30) return `hace ${Math.floor(diff/7)}sem`;
  return `hace ${Math.floor(diff/30)}m`;
};

export default function PioneroChallengeCard({ compact = false }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrOpen, setQrOpen] = useState(false);

  const isEligible = user?.role === "lawyer" && ["pionero", "embajador_pionero", "founder"].includes(user?.plan);

  useEffect(() => {
    if (!isEligible) { setLoading(false); return; }
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/plans/status").then((x) => x.data),
          api.get("/plans/my-referrals").then((x) => x.data),
        ]);
        if (cancelled) return;
        setStatus(s);
        setRefs(r.referrals || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchOnce();
    // Polling 15s: mantiene la lista y la barra de progreso al día sin reload.
    const iv = setInterval(fetchOnce, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [isEligible, user?.id]);

  if (!isEligible || loading || !status || status.plan === "embajador_pionero") {
    // Ocultamos si el reto ya se completó (embajador) o si no aplica
    return null;
  }

  const required = status.pionero_refs_required || 15;
  const validated = status.pionero_refs_validated || 0;
  const total = refs.length;
  const pending = total - validated;
  const pct = Math.min(100, Math.round((validated / required) * 100));
  const daysLeft = status.pionero_days_left ?? null;
  const complete = status.pionero_refs_complete;
  const expired = status.pionero_challenge_expired;
  const refUrl = buildRefUrl(user?.referral_code);

  return (
    <div
      className="panel p-6 relative overflow-hidden"
      data-testid="pionero-challenge-card"
    >
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-amber-400/5 rounded-full blur-3xl -translate-y-24 translate-x-24 pointer-events-none"/>

      <div className="relative">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400/25 to-amber-500/10 border border-amber-400/40 flex items-center justify-center shrink-0">
              <Trophy size={20} className="text-amber-300"/>
            </div>
            <div>
              <div className="label-eyebrow text-amber-300">Mi Reto Pionero</div>
              <h3 className="serif text-2xl leading-tight">
                {complete ? "¡Reto completo! 🎉" :
                 expired ? "Reto expirado" :
                 `Llevas ${validated} de ${required} colegas validados`}
              </h3>
            </div>
          </div>
          {status.pionero_number && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-200 font-semibold uppercase tracking-wider">
              <Trophy size={10}/> Pionero N°{status.pionero_number}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className={`font-semibold ${complete ? "text-emerald-300" : "text-amber-200"}`}>
              {validated} / {required} validados · {pct}%
            </span>
            {daysLeft !== null && !expired && (
              <span className={`inline-flex items-center gap-1 ${daysLeft <= 7 ? "text-red-300" : "text-muted-foreground"}`}>
                <Clock size={11}/> Te quedan {daysLeft} día{daysLeft === 1 ? "" : "s"}
              </span>
            )}
            {expired && <span className="text-red-300 inline-flex items-center gap-1"><Clock size={11}/> Vencido</span>}
          </div>
          <div className="h-3 rounded-full bg-white/5 overflow-hidden border border-white/10">
            <div
              className={`h-full transition-all duration-700 ease-out ${
                complete ? "bg-gradient-to-r from-emerald-500 to-emerald-300" :
                pct >= 60 ? "bg-gradient-to-r from-amber-500 to-amber-300" :
                "bg-gradient-to-r from-orange-500 to-amber-400"
              }`}
              style={{ width: `${pct}%` }}
              data-testid="progress-bar-fill"
            />
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {complete ? (
              <span className="text-emerald-300">¡Completaste el reto! Sube al plan <Link to="/planes" className="underline hover:text-emerald-200">Pionero Fundador</Link> para desbloquear el 0% vitalicio.</span>
            ) : expired ? (
              <span className="text-red-300">Tu ventana de 3 meses venció. Tu cuenta pasará a Colega Free en la próxima ejecución del cron.</span>
            ) : (
              <>Necesitas <strong className="text-foreground">{required - validated}</strong> referido{required - validated === 1 ? "" : "s"} validado{required - validated === 1 ? "" : "s"} más para desbloquear 0% comisión vitalicia.
              <br/><span className="text-amber-300/90 text-[10px] mt-1 inline-flex items-center gap-1"><Info size={10}/> Sólo cuentan quienes se unan como Pionero o Pionero Fundador. Los que se queden en plan Free no suman a la meta.</span></>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="mt-5 flex flex-wrap gap-2">
          <Link
            to="/app/invitar"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md bg-amber-500 hover:bg-amber-400 text-amber-950 transition-colors"
            data-testid="cta-invitar"
          >
            <Users size={13}/> Invitar más colegas
          </Link>
          {refUrl && (
            <button
              onClick={() => setQrOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-white/15 hover:bg-white/5"
              data-testid="cta-qr"
            >
              <QrCode size={13}/> Ver mi código QR de invitación
            </button>
          )}
        </div>

        {/* Referrals list */}
        {!compact && refs.length > 0 && (
          <div className="mt-6 border-t border-white/5 pt-5" data-testid="referrals-list">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs label-eyebrow">Colegas que se registraron con tu código</div>
              <div className="text-[10px] text-muted-foreground">
                {total} total · {validated} verificados · {pending} pendientes
              </div>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {refs.map((r) => (
                <div
                  key={r.id}
                  className={`flex items-center gap-3 p-2.5 rounded-md border ${
                    r.is_validated
                      ? "bg-emerald-400/5 border-emerald-400/20"
                      : "bg-white/[0.02] border-white/5"
                  }`}
                  data-testid={`ref-row-${r.id}`}
                >
                  {r.is_validated ? (
                    <CheckCircle2 size={16} className="text-emerald-400 shrink-0"/>
                  ) : (
                    <CircleDashed size={16} className="text-amber-300/70 shrink-0"/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.name || r.email}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {r.email} · Se registró {daysAgo(r.created_at)}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
                    r.is_validated
                      ? "bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
                      : "bg-amber-400/10 text-amber-300 border border-amber-400/25"
                  }`}>
                    {r.is_validated ? "Verificado" : !r.verified ? "Pendiente verificación" : "Perfil incompleto"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {!compact && refs.length === 0 && (
          <div className="mt-6 border-t border-white/5 pt-5 text-center py-6" data-testid="referrals-empty">
            <Users size={22} className="text-muted-foreground/40 mx-auto"/>
            <div className="mt-2 text-sm text-muted-foreground">
              Aún nadie se ha registrado con tu código. <Link to="/app/invitar" className="text-amber-300 hover:text-amber-200 underline">Envía tu primera invitación</Link>.
            </div>
          </div>
        )}
      </div>

      {qrOpen && refUrl && (
        <QRModal
          url={refUrl}
          code={user.referral_code}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}

function QRModal({ url, code, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const downloadPng = () => {
    // Convert the inline SVG to a downloadable PNG
    const svg = document.getElementById("pionero-qr-svg");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      canvas.toBlob((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `colegalink-invitacion-${code || "pionero"}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }, "image/png");
      URL.revokeObjectURL(svgUrl);
    };
    img.src = svgUrl;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <div
        className="panel max-w-sm w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          data-testid="qr-modal-close"
        >
          <X size={18}/>
        </button>
        <div className="label-eyebrow text-amber-300">Reto Pionero</div>
        <h3 className="serif text-2xl mt-1">Tu código QR de invitación</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Comparte este QR en WhatsApp, redes o impreso. Cuando alguien lo escanee, entrará directo al registro con tu código precargado.
        </p>

        <div className="mt-5 p-5 bg-white rounded-md flex items-center justify-center" data-testid="qr-container">
          <QRCodeSVG
            id="pionero-qr-svg"
            value={url}
            size={220}
            level="H"
            includeMargin={false}
            imageSettings={{
              src: "/logo.png",
              height: 44,
              width: 44,
              excavate: true,
            }}
          />
        </div>

        <div className="mt-4 p-2.5 rounded-md bg-white/5 border border-white/10 text-[11px] font-mono break-all" data-testid="qr-url">
          {url}
        </div>

        <div className="mt-4 flex gap-2">
          <button
            onClick={copy}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-md border border-white/15 hover:bg-white/5"
            data-testid="qr-copy"
          >
            {copied ? <Check size={13} className="text-emerald-400"/> : <Copy size={13}/>}
            {copied ? "Copiado" : "Copiar link"}
          </button>
          <button
            onClick={downloadPng}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-amber-500 hover:bg-amber-400 text-amber-950"
            data-testid="qr-download"
          >
            <Download size={13}/> Descargar PNG
          </button>
        </div>
      </div>
    </div>
  );
}
